import { usePendingCrudCount } from '@hooks/usePendingCrudCount';
import { useSyncStatus } from '@hooks/useSyncStatus';
import React from 'react';

/**
 * Dev-mode-only overlay showing the live PowerSync upload queue depth.
 * Rendered in the bottom-right corner; returns null in production builds.
 *
 * Covers Phase 3 of the Offline-First Implementation Plan:
 * "Queue monitoring: add a dev-mode overlay showing pending write queue depth."
 */
export const PowerSyncDevOverlay: React.FC = () => {
  if (process.env.NODE_ENV !== 'development') return null;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { connected, connecting, lastSyncedAt, hasSynced } = useSyncStatus();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const pendingCount = usePendingCrudCount();

  const statusColor = connected
    ? 'text-emerald-400'
    : connecting
      ? 'text-amber-400'
      : 'text-red-400';

  const statusLabel = connected
    ? 'connected'
    : connecting
      ? 'connecting'
      : 'offline';

  const lastSync = lastSyncedAt
    ? new Date(lastSyncedAt).toLocaleTimeString()
    : hasSynced
      ? 'synced'
      : 'never';

  return (
    <div className="fixed bottom-4 right-4 z-[9999] rounded-lg border border-neutral-700 bg-neutral-900/90 px-3 py-2 text-[11px] font-mono text-neutral-300 shadow-lg backdrop-blur-sm select-none pointer-events-none">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
        PowerSync
      </div>
      <div className="flex flex-col gap-0.5">
        <div>
          status: <span className={statusColor}>{statusLabel}</span>
        </div>
        <div>
          queue:{' '}
          <span
            className={pendingCount > 0 ? 'text-amber-400' : 'text-emerald-400'}
          >
            {pendingCount} pending
          </span>
        </div>
        <div>
          last sync: <span className="text-neutral-400">{lastSync}</span>
        </div>
      </div>
    </div>
  );
};
